import { Badge, Image, Skeleton, Space } from "antd";
import React from "react";
import MediaError from "../../assets/images/mediaError.jpg";
import VideoPreviewComponent from "./video/VideoPreview";

const ReusableFilePreview = React.memo((props) => {
  const { setFileList, fileList, shape, direction, customClasses, userStyles, initialValues } = props;
  let stylesProps = {};
  if (shape === "circle") {
    stylesProps = { minHeight: "53px", alignItems: "end" };
  }
  if (userStyles) {
    stylesProps = { ...stylesProps, ...userStyles };
  }

  const handleDelete = (uid) => {
    setFileList(fileList.filter((file) => file.uid !== uid));
  };

  const CompletedState = React.memo(({ file }) => {
    if (shape === "circle") {
      return (
        <Badge
          count={
            !file?.hideCloseIcon ? (
              <div className="actionIconsSprite previewCloseIcon" data-cy="preview-close-icon" onClick={() => handleDelete(file.uid)} />
            ) : null
          }
        >
          {file.type?.startsWith("image/") && <ImagePreview shape={shape} file={file} />}
          {file.type?.startsWith("video/") && <VideoPreview file={file} shape={shape} />}
          {(file.type?.startsWith("application/") || file.type?.startsWith("text/")) && <DocumentPreview file={file} shape={shape} />}
          {file.type?.startsWith("audio/") && <AudioPreview file={file} shape={shape} />}
        </Badge>
      );
    } else if (shape === "card") {
      return (
        <>
          <div className="hs-mw-85 ">
            {file.type?.startsWith("image/") && <ImagePreview shape={shape} file={file} />}
            {file.type?.startsWith("video/") && <VideoPreview file={file} shape={shape} />}
            {(file.type?.startsWith("application/") || file.type?.startsWith("text/")) && <DocumentPreview file={file} shape={shape} />}
            {file.type?.startsWith("audio/") && <AudioPreview file={file} shape={shape} />}
          </div>
          {initialValues?.disabled ? <></> : <div className="actionIconsSprite binOutlined" onClick={() => handleDelete(file.uid)} />}
        </>
      );
    } else if (shape === "square") {
      return (
        <>
          {file.type?.startsWith("image/") && (
            <div className="hs-w-40-px">
              <div className="hs-square-image-preview">
                <ImagePreview shape={shape} file={file} />
              </div>
            </div>
          )}
          {file.type?.startsWith("video/") && <VideoPreview file={file} shape={shape} />}
          {(file.type?.startsWith("application/") || file.type?.startsWith("text/")) && <DocumentPreview file={file} shape={shape} />}
          {file.type?.startsWith("audio/") && <AudioPreview file={file} shape={shape} />}
        </>
      );
    }
  });

  const UploadingState = () => {
    if (shape === "circle") {
      return <CircleUploadingState />;
    } else if (shape === "card") {
      return <ActiveImageSkeleton />;
    }
  };

  return (
    <Space direction={direction} className={`w-100 overflow-auto pt-2 pb-2 ${customClasses}`} style={stylesProps}>
      {fileList &&
        fileList.map((file) => {
          return (
            <div key={crypto.randomUUID()} className="d-flex gap-2" data-cy="file-preview">
              {file.status === "done" ? <CompletedState file={file} /> : <UploadingState />}
            </div>
          );
        })}
    </Space>
  );
});

const CircleUploadingState = () => {
  return (
    <div className="d-flex align-items-center circleWrapper">
      <ActiveImageSkeleton className="circleSkeletonLoading" />
    </div>
  );
};

function ActiveImageSkeleton(props) {
  return <Skeleton.Image className={`w-100 rounded ${props.className}`} active />;
}

const DocumentPreview = React.memo(({ file, shape }) => {
  const isNotSquareShape = shape === "square" ? false : true;
  return (
    <a
      href={file.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`${isNotSquareShape ? "me-2" : ""}`}
      style={{
        borderRadius: 12,
        display: "block",
        minWidth: isNotSquareShape ? "7rem" : "0rem",
        textAlign: "center",
        textDecoration: "none",
        color: "black",
      }}
    >
      <div className="d-flex align-items-center justify-content-start">
        <span className={`${file.type === "application/pdf" ? "pdf-icon" : "doc-icon"} staticIconsSprite pdf-icon`} data-cy="pdf-icon"></span>
        {isNotSquareShape ? <>{file.name}</> : null}
      </div>
    </a>
  );
});

const AudioPreview = React.memo(({ file }) => {
  return (
    <audio
      src={file.url}
      controls
      className="mt-1"
      style={{
        borderRadius: 12,
      }}
    />
  );
});

const VideoPreview = React.memo(({ file, shape }) => {
  if (shape === "circle") {
    return (
      <VideoPreviewComponent
        src={file.url}
        width={50}
        className="mt-1"
        style={{
          borderRadius: 12,
          margin: -1,
        }}
      />
    );
  } else {
    return (
      <VideoPreviewComponent
        src={file.url}
        width={300}
        className="mt-1"
        style={{
          borderRadius: 12,
          margin: -1,
        }}
      />
    );
  }
});

const ImagePreview = React.memo(({ file, shape }) => {
  if (shape === "circle") {
    return (
      <Image
        data-cy="image-preview"
        wrapperClassName="d-flex align-items-center circleWrapper"
        placeholder={<CircleUploadingState />}
        src={file.url}
        preview={{
          mask: <div />,
          toolbarRender: () => null,
          maskClassName: "previewMask",
        }}
      />
    );
  } else if (shape === "card") {
    return (
      <Image
        preview={false}
        className="rounded preview"
        src={file.url}
        alt="Preview"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = MediaError;
        }}
      />
    );
  } else if (shape === "square") {
    return (
      <Image
        preview={{
          mask: <div />,
          toolbarRender: () => null,
          maskClassName: "previewMask",
        }}
        src={file.url}
        alt="Preview"
      />
    );
  }
});

export default ReusableFilePreview;
